import type { HTMLAttributes } from 'react'
import { Link } from '@inertiajs/react'
import { Card as DsCard } from '@digdir/designsystemet-react'
import clsx from 'clsx'
import { Icon } from './Icon'

type DeltaDirection = 'up' | 'down'

type Delta = {
  direction: DeltaDirection
  text: string
}

type CardProps = {
  /** Short label rendered above the KPI value — uppercase caption style */
  title?: string
  /** Primary metric value (string or number) */
  kpi?: string | number
  /**
   * Trend indicator shown below the KPI.
   * `direction` drives the color modifier (.card__delta--up / --down) and
   * the arrow icon; `text` is the human-readable label.
   */
  delta?: Delta
  /** Arbitrary content rendered inside the card after the KPI block */
  children?: React.ReactNode
  /**
   * When set, the whole card becomes a navigation target (Inertia Link) with a
   * hover affordance — used for clickable KPI tiles. Text color is inherited
   * (not the default link color) so the KPI value looks unchanged.
   */
  href?: string
  /** Additional CSS classes merged after the .card classes */
  className?: string
} & Omit<HTMLAttributes<HTMLDivElement>, 'title'>

// Rotate the decorative down chevron for an upward trend.
const DELTA_ICON: Record<DeltaDirection, { iconStyle?: React.CSSProperties }> = {
  up: { iconStyle: { transform: 'rotate(180deg)', display: 'inline-block' } },
  down: { iconStyle: undefined },
}

/** DS container with existing KPI content and layout hooks. */
export function Card({ title, kpi, delta, children, href, className, ...rest }: CardProps) {
  const body = (
    <>
      {title !== undefined && (
        <div className="card__title">{title}</div>
      )}

      {kpi !== undefined && (
        <div className="card__kpi">{kpi}</div>
      )}

      {delta !== undefined && (
        <div
          className={
            'card__delta' +
            (delta.direction === 'up' ? ' card__delta--up' : ' card__delta--down')
          }
        >
          {/* Icon is decorative — the text conveys the trend meaning */}
          <span style={DELTA_ICON[delta.direction].iconStyle}>
            <Icon name="chevronD" size={12} />
          </span>
          {delta.text}
        </div>
      )}

      {children}
    </>
  )

  if (href !== undefined) {
    return (
      <DsCard {...rest} asChild className={clsx('card', 'card--link', className)}>
        <Link href={href}>{body}</Link>
      </DsCard>
    )
  }

  // An empty delegate target prevents DS from turning a heading link into a background action.
  return <DsCard {...rest} data-clickdelegatefor="" className={clsx('card', className)}>{body}</DsCard>
}
